import React, { useEffect, useState } from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import toast from "react-hot-toast";
import { facilityAdmin } from "../../api";
import BRAND from "../../brand";
import ExportMenu from "./ExportMenu";

// "Categories & Traffic" tab (white-label builds): which ticket prefixes (categories) are used,
// where the traffic is by zone and station, and a drill-down into one station's counters.
// Data: /reports/traffic/ and /reports/traffic/station/.

const P = BRAND.colorPrimary;
const A = BRAND.colorAccent;
const PALETTE = [P, A, "#D97706", "#7C3AED", "#DB2777", "#0891B2", "#64748B"];

const iso = (offset = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
};
const hourLabel = (h) => `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? "a" : "p"}`;
const hourLong = (h) => `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? "AM" : "PM"}`;
const mins = (v) => (v == null ? "–" : `${v % 1 === 0 ? v : v.toFixed(1)} min`);
const shortDate = (s) => { const d = new Date(`${s}T00:00:00`); return `${d.getDate()}/${d.getMonth() + 1}`; };
const hrs = (m) => (m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`);

const Card = ({ title, sub, right, children, style }) => (
  <div style={{ ...S.card, ...style }}>
    <div style={S.head}><div><div style={S.title}>{title}</div>{sub && <div style={S.sub}>{sub}</div>}</div>{right}</div>
    {children}
  </div>
);

function RangeBar({ from, to, setFrom, setTo, exportParams }) {
  const quick = (n) => { setFrom(iso(-(n - 1))); setTo(iso()); };
  return (
    <div style={S.filters}>
      <label style={S.lab}>From <input type="date" style={S.input} value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} /></label>
      <label style={S.lab}>To <input type="date" style={S.input} value={to} min={from} max={iso()} onChange={(e) => e.target.value && setTo(e.target.value)} /></label>
      {[[7, "7 days"], [14, "14 days"], [30, "30 days"], [90, "90 days"]].map(([n, l]) => <button key={n} style={S.chip} onClick={() => quick(n)}>{l}</button>)}
      <ExportMenu style={{ marginLeft: "auto" }} report="traffic" params={{ date_from: from, date_to: to, ...exportParams }} />
    </div>
  );
}

export default function TrafficInsight() {
  const [from, setFrom] = useState(iso(-13));
  const [to, setTo] = useState(iso());
  const [d, setD] = useState(null);
  const [zone, setZone] = useState("");
  const [station, setStation] = useState(null); // {id, name} being inspected
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    facilityAdmin.trafficReport({ date_from: from, date_to: to })
      .then((r) => { if (live) setD(r.data); })
      .catch(() => { if (live) toast.error("Failed to load the traffic report."); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [from, to]);

  useEffect(() => {
    if (!station) { setDetail(null); return undefined; }
    let live = true;
    setDetail(null);
    facilityAdmin.trafficStationReport({ station: station.id, date_from: from, date_to: to })
      .then((r) => { if (live) setDetail(r.data); })
      .catch(() => { if (live) toast.error("Failed to load the station detail."); });
    return () => { live = false; };
  }, [station, from, to]);

  if (!d) return <div style={S.sub}>{loading ? "Loading…" : ""}</div>;

  // ── one station in detail ──────────────────────────────────────────
  if (station) {
    const sm = detail?.summary;
    const hourData = detail ? detail.hours.map((v, h) => ({ hour: hourLabel(h), v, h })).filter((x) => x.v || (x.h >= 7 && x.h <= 18)) : [];
    const peak = hourData.reduce((b, x) => (x.v > (b?.v || 0) ? x : b), null);
    const maxServed = Math.max(1, ...(detail?.counters || []).map((c) => c.served));
    return (
      <div style={S.col}>
        <div style={S.filters}>
          <button style={S.back} onClick={() => setStation(null)}>← All stations</button>
          <b style={{ fontSize: 18, color: "var(--text-primary)" }}>{station.name}</b>
          <span style={S.sub}>{detail?.station.zone}</span>
        </div>
        <RangeBar from={from} to={to} setFrom={setFrom} setTo={setTo} exportParams={{ station: station.id }} />
        {!detail ? <div style={S.sub}>Loading…</div> : (
          <>
            <div style={S.stats}>
              {[["Tickets", sm.tickets], ["Finished here", sm.served], ["Average wait", mins(sm.avg_wait)], ["Average service", mins(sm.avg_service)]].map(([l, v]) => (
                <div key={l} style={{ ...S.card, padding: "14px 16px" }}><div style={S.sub}>{l}</div><div style={S.big}>{v}</div></div>
              ))}
            </div>

            <Card title="Counters" sub="Visits finished, waits and service time for each counter at this station, over the chosen dates.">
              <div style={S.counterGrid}>
                {detail.counters.map((c) => (
                  <div key={c.counter_id || "none"} style={{ ...S.counter, ...(c.counter_id ? {} : { borderStyle: "dashed" }) }}>
                    <div style={S.counterHead}>
                      <b>{c.counter_name}</b>
                      {c.active === false && <span style={S.tag}>switched off</span>}
                    </div>
                    <div style={S.sub}>{c.staff.length ? c.staff.join(", ") : "No staff recorded"}</div>
                    <div style={S.track}><div style={{ ...S.fill, width: `${(c.served / maxServed) * 100}%`, background: P }} /></div>
                    <div style={S.kv}>
                      <span>Visits finished</span><b>{c.served} <span style={S.sub}>({c.share_of_station}%)</span></b>
                      <span>Calls</span><b>{c.calls}</b>
                      <span>Skipped</span><b>{c.skipped}</b>
                      <span>Held</span><b>{c.held}</b>
                      <span>Average wait</span><b>{mins(c.avg_wait)}</b>
                      <span>Average service</span><b>{mins(c.avg_service)}</b>
                      <span>Time serving</span><b>{c.busy_minutes ? hrs(c.busy_minutes) : "–"}</b>
                    </div>
                    {!c.counter_id && <div style={S.sub}>Calls made before a counter was chosen (staff pick one at the top of the panel).</div>}
                  </div>
                ))}
              </div>
            </Card>

            <div style={S.row2}>
              <Card title="Busiest hours at this station" sub={peak && peak.v ? `Peak is ${hourLong(peak.h)}: about ${peak.v} arrivals an hour on average.` : "Average arrivals per hour."}>
                <div style={{ height: 190 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={hourData} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                      <XAxis dataKey="hour" tick={{ fontSize: 10, fill: "var(--text-secondary)" }} interval={0} />
                      <YAxis tick={{ fontSize: 11, fill: "var(--text-secondary)" }} />
                      <Tooltip formatter={(v) => [v, "Arrivals per hour"]} />
                      <Bar dataKey="v" radius={[3, 3, 0, 0]}>{hourData.map((x) => <Cell key={x.h} fill={peak && x.h === peak.h ? A : `${A}77`} />)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
              <Card title="Categories at this station" sub="Arrivals by ticket prefix.">
                {detail.prefixes.map((p, i) => (
                  <div key={p.prefix} style={S.bar}>
                    <span style={{ ...S.dot, background: PALETTE[i % PALETTE.length] }} /><b style={{ width: 40 }}>{p.prefix}</b>
                    <div style={S.track}><div style={{ ...S.fill, width: `${(p.tickets / detail.prefixes[0].tickets) * 100}%`, background: PALETTE[i % PALETTE.length] }} /></div>
                    <span style={S.barVal}>{p.tickets}</span>
                  </div>
                ))}
              </Card>
            </div>
          </>
        )}
      </div>
    );
  }

  // ── overview ───────────────────────────────────────────────────────
  const stations = d.stations.filter((s) => !zone || s.zone_id === zone);
  const maxT = Math.max(1, ...stations.map((s) => s.tickets));
  const colour = (p) => PALETTE[Math.max(0, d.prefixes.findIndex((x) => x.prefix === p)) % PALETTE.length];

  return (
    <div style={{ ...S.col, opacity: loading ? 0.6 : 1 }}>
      <RangeBar from={from} to={to} setFrom={setFrom} setTo={setTo} />
      <div style={S.sub}>{d.date_from} to {d.date_to} ({d.days} day{d.days !== 1 ? "s" : ""}) · {d.total_tickets} ticket{d.total_tickets !== 1 ? "s" : ""}</div>

      <div style={S.row2}>
        <Card title="Ticket categories" sub="Each ticket prefix, with the category names set on Insurance / Prefixes.">
          {d.prefixes.length === 0 ? <div style={S.empty}>No tickets in this range.</div> : (
            <div style={S.donutWrap}>
              <div style={{ width: 160, height: 160, position: "relative", flexShrink: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart><Pie data={d.prefixes} dataKey="tickets" nameKey="prefix" innerRadius={48} outerRadius={74} paddingAngle={2} stroke="none">
                    {d.prefixes.map((p, i) => <Cell key={p.prefix} fill={PALETTE[i % PALETTE.length]} />)}
                  </Pie></PieChart>
                </ResponsiveContainer>
                <div style={S.donutMid}><b style={{ fontSize: 24 }}>{d.total_tickets}</b><span style={S.sub}>Tickets</span></div>
              </div>
              <table style={{ ...S.table, flex: 1, minWidth: 260 }}>
                <thead><tr>{["Prefix", "Category", "Tickets", "Share", "Avg wait"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {d.prefixes.map((p, i) => (
                    <tr key={p.prefix}>
                      <td style={S.td}><span style={{ ...S.dot, background: PALETTE[i % PALETTE.length] }} /> <b>{p.prefix}</b></td>
                      <td style={S.td}>{p.label}</td><td style={S.td}>{p.tickets}</td><td style={S.td}>{p.share}%</td><td style={S.td}>{mins(p.avg_wait)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        <Card title="Category trend" sub={`Tickets per day for the ${d.trend_prefixes.length} busiest prefix${d.trend_prefixes.length !== 1 ? "es" : ""}.`}>
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.trend.map((x) => ({ ...x, day: shortDate(x.date) }))} margin={{ top: 8, right: 12, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="var(--surface-border)" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "var(--text-secondary)" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "var(--text-secondary)" }} />
                <Tooltip />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                {d.trend_prefixes.map((p) => <Area key={p} type="monotone" dataKey={p} name={p} stroke={colour(p)} fill={`${colour(p)}22`} strokeWidth={2} />)}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card title="Where the traffic is" sub="Click a zone to narrow the list, then a station to see its counters.">
        <div style={S.zones}>
          <button style={{ ...S.zone, ...(zone === "" ? S.zoneOn : {}) }} onClick={() => setZone("")}>All zones<b>{d.stations.reduce((n, s) => n + s.tickets, 0)}</b></button>
          {d.zones.map((z) => (
            <button key={z.zone_id} style={{ ...S.zone, ...(zone === z.zone_id ? S.zoneOn : {}) }} onClick={() => setZone(zone === z.zone_id ? "" : z.zone_id)}>
              {z.zone_name}<span style={S.sub}>{z.stations} station{z.stations !== 1 ? "s" : ""} · {z.share}% of tickets</span><b>{z.tickets}</b>
            </button>
          ))}
        </div>
        <table style={S.table}>
          <thead><tr>{["Station", "Traffic", "Tickets", "Finished", "Avg wait", "Avg service", "Busiest hour", ""].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {stations.map((s) => (
              <tr key={s.station_id} style={S.rowClick} onClick={() => setStation({ id: s.station_id, name: s.station_name })}>
                <td style={S.td}><b style={{ color: P }}>{s.station_name}</b><div style={S.sub}>{s.zone_name} · {s.counters} counter{s.counters !== 1 ? "s" : ""}</div></td>
                <td style={{ ...S.td, width: "22%" }}><div style={S.track}><div style={{ ...S.fill, width: `${(s.tickets / maxT) * 100}%`, background: P }} /></div><div style={S.sub}>{s.share}% of tickets</div></td>
                <td style={S.td}>{s.tickets}</td><td style={S.td}>{s.served}</td>
                <td style={S.td}>{mins(s.avg_wait)}</td><td style={S.td}>{mins(s.avg_service)}</td>
                <td style={S.td}>{s.busiest_hour == null ? "–" : hourLong(s.busiest_hour)}</td>
                <td style={{ ...S.td, color: P, fontWeight: 600 }}>Counters →</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

const S = {
  col: { display: "flex", flexDirection: "column", gap: 16 },
  sub: { color: "var(--text-secondary)", fontSize: 12, marginTop: 2 },
  empty: { color: "var(--text-secondary)", fontSize: 13, padding: "26px 0", textAlign: "center" },
  big: { fontSize: 26, fontWeight: 700, color: "var(--text-primary)", marginTop: 4 },
  filters: { display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" },
  lab: { display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-secondary)" },
  input: { background: "var(--input-bg)", border: "1px solid var(--input-border)", color: "var(--text-primary)", borderRadius: 10, padding: "8px 10px", fontSize: 13, fontFamily: "inherit" },
  chip: { background: `${P}18`, color: P, border: "none", borderRadius: 20, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  back: { background: "transparent", color: P, border: `1px solid ${P}`, borderRadius: 10, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  stats: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 },
  card: { background: "var(--surface-bg)", border: "1px solid var(--surface-border)", borderRadius: 14, padding: "16px 18px", boxShadow: "var(--card-shadow)" },
  head: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 10 },
  title: { fontSize: 14, fontWeight: 700, color: "var(--text-primary)" },
  row2: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 },
  donutWrap: { display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" },
  donutMid: { position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "var(--text-primary)" },
  dot: { width: 9, height: 9, borderRadius: "50%", display: "inline-block", marginRight: 4 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13, color: "var(--text-primary)" },
  th: { textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-secondary)", padding: "8px 10px", borderBottom: "1px solid var(--surface-border)" },
  td: { padding: "10px", borderBottom: "1px solid var(--surface-border)", verticalAlign: "middle" },
  rowClick: { cursor: "pointer" },
  zones: { display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12 },
  zone: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2, minWidth: 140, background: "var(--input-bg)", border: "1px solid var(--surface-border)", borderRadius: 12, padding: "10px 14px", fontSize: 13, fontWeight: 600, color: "var(--text-primary)", cursor: "pointer", fontFamily: "inherit", textAlign: "left" },
  zoneOn: { borderColor: P, background: `${P}18` },
  track: { flex: 1, height: 10, background: "var(--surface-border)", borderRadius: 6, overflow: "hidden", margin: "6px 0" },
  fill: { height: "100%", borderRadius: 6 },
  bar: { display: "flex", alignItems: "center", gap: 8, margin: "8px 0", fontSize: 13, color: "var(--text-primary)" },
  barVal: { width: 36, textAlign: "right", fontWeight: 600 },
  counterGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 },
  counter: { border: "1px solid var(--surface-border)", borderRadius: 12, padding: "12px 14px", color: "var(--text-primary)", fontSize: 13 },
  counterHead: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  tag: { fontSize: 11, background: "#D9770622", color: "#D97706", borderRadius: 20, padding: "2px 8px", fontWeight: 600 },
  kv: { display: "grid", gridTemplateColumns: "1fr auto", gap: "5px 10px", marginTop: 6 },
};
